import React, { useCallback, useEffect, useState } from 'react';
import api from '../api';
import StatusAmpel from '../components/StatusAmpel';
import { InstanzUebersicht, formatiereBytes, formatiereZeit } from '../types';

interface InstanzenPageProps {
  onOeffnen: (slug: string) => void;
}

const InstanzenPage: React.FC<InstanzenPageProps> = ({ onOeffnen }) => {
  const [instanzen, setInstanzen] = useState<InstanzUebersicht[]>([]);
  const [laden, setLaden] = useState(true);
  const [fehler, setFehler] = useState('');
  const [beschaeftigt, setBeschaeftigt] = useState<string | null>(null);

  const laden_ = useCallback(async () => {
    setFehler('');
    try {
      const res = await api.get<InstanzUebersicht[]>('/api/instanzen');
      setInstanzen(res.data);
    } catch (err: any) {
      setFehler(err.response?.data?.detail || 'Instanzen konnten nicht geladen werden.');
    } finally {
      setLaden(false);
    }
  }, []);

  useEffect(() => {
    laden_();
  }, [laden_]);

  const uebernehmen = async (slug: string) => {
    setBeschaeftigt(slug);
    setFehler('');
    try {
      await api.post('/api/instanzen/uebernehmen', { slug });
      await laden_();
    } catch (err: any) {
      setFehler(err.response?.data?.detail || `'${slug}' konnte nicht übernommen werden.`);
    } finally {
      setBeschaeftigt(null);
    }
  };

  const schalten = async (slug: string, aktion: 'stoppen' | 'starten') => {
    setBeschaeftigt(slug);
    setFehler('');
    try {
      await api.post(`/api/instanzen/${slug}/${aktion}`);
      await laden_();
    } catch (err: any) {
      setFehler(err.response?.data?.detail || `Aktion '${aktion}' fehlgeschlagen.`);
    } finally {
      setBeschaeftigt(null);
    }
  };

  const registrierte = instanzen.filter(i => i.registriert && i.typ === 'KUNDE');
  const gefundene = instanzen.filter(i => !i.registriert && i.typ === 'KUNDE');
  const system = instanzen.filter(i => i.typ === 'SYSTEM');

  if (laden) {
    return <div className="k-hinweis">Instanzen werden geladen …</div>;
  }

  return (
    <div className="k-seite">
      <div className="k-seite__kopf">
        <div>
          <span className="k-eyebrow">{'// fleet / instanzen'}</span>
          <h1 className="k-h1">Instanzen</h1>
        </div>
        <button className="k-btn k-btn--geist" onClick={laden_}>
          Aktualisieren
        </button>
      </div>

      {fehler && <div className="k-fehler">{fehler}</div>}

      {instanzen.length === 0 && (
        <div className="k-empty">
          <span className="k-corner k-corner--tl">{'// fleet / instanzen'}</span>
          <span className="k-corner k-corner--tr">registry leer</span>
          <h2 className="k-empty__title">Keine Instanzen gefunden</h2>
          <p className="k-empty__hint">
            Unter <code>/opt/kunden</code> liegt kein Kundenstack. Neue Umgebungen
            werden mit der geführten Provisionierung angelegt (folgt in M3).
          </p>
        </div>
      )}

      {registrierte.length > 0 && (
        <section className="k-block">
          <h2 className="k-h2">Registriert</h2>
          <Tabelle
            zeilen={registrierte}
            beschaeftigt={beschaeftigt}
            onOeffnen={onOeffnen}
            onSchalten={schalten}
          />
        </section>
      )}

      {gefundene.length > 0 && (
        <section className="k-block">
          <h2 className="k-h2">Auf dem Server gefunden</h2>
          <p className="k-block__hint">
            Diese Stacks liegen unter <code>/opt/kunden</code>, sind dem Fleet aber
            noch nicht bekannt. Übernehmen macht sie steuerbar (Backups, Rollout).
          </p>
          <Tabelle
            zeilen={gefundene}
            beschaeftigt={beschaeftigt}
            onOeffnen={onOeffnen}
            onUebernehmen={uebernehmen}
          />
        </section>
      )}

      {system.length > 0 && (
        <section className="k-block">
          <h2 className="k-h2">System / CI-verwaltet</h2>
          <p className="k-block__hint">
            Prod und erptest. Deploys laufen weiterhin über die CI-Pipeline;
            Fleet bietet hier Status, Backup und Stop/Start.
          </p>
          <Tabelle
            zeilen={system}
            beschaeftigt={beschaeftigt}
            onOeffnen={onOeffnen}
            onSchalten={schalten}
          />
        </section>
      )}
    </div>
  );
};

interface TabelleProps {
  zeilen: InstanzUebersicht[];
  beschaeftigt: string | null;
  onOeffnen: (slug: string) => void;
  onSchalten?: (slug: string, aktion: 'stoppen' | 'starten') => void;
  onUebernehmen?: (slug: string) => void;
}

const Tabelle: React.FC<TabelleProps> = ({
  zeilen,
  beschaeftigt,
  onOeffnen,
  onSchalten,
  onUebernehmen,
}) => (
  <div className="k-tabelle__rahmen">
    <table className="k-tabelle">
      <thead>
        <tr>
          <th>Status</th>
          <th>Slug</th>
          <th>Firma</th>
          <th>Version</th>
          <th>Datenbank</th>
          <th>Letztes Backup</th>
          <th className="k-tabelle__aktionen">Aktionen</th>
        </tr>
      </thead>
      <tbody>
        {zeilen.map(i => (
          <tr key={i.slug}>
            <td>
              <StatusAmpel status={i.status} laeuft={i.laeuft} />
            </td>
            <td>
              <button className="k-link" onClick={() => onOeffnen(i.slug)}>
                {i.slug}
              </button>
              {i.url && (
                <a className="k-extern" href={i.url} target="_blank" rel="noreferrer">
                  ↗
                </a>
              )}
              {i.umgebung === 'PROD'
                ? <span className="k-badge k-badge--prod">PROD</span>
                : <span className="k-badge">TEST</span>}
            </td>
            <td>{i.firmenname}</td>
            <td className="k-mono">{i.image_tag || '—'}</td>
            <td className="k-mono">{formatiereBytes(i.db_groesse_bytes)}</td>
            <td className="k-mono">{formatiereZeit(i.letztes_backup)}</td>
            <td className="k-tabelle__aktionen">
              <button className="k-btn k-btn--geist" onClick={() => onOeffnen(i.slug)}>
                Öffnen
              </button>
              {onUebernehmen && (
                <button
                  className="k-btn"
                  disabled={beschaeftigt === i.slug}
                  onClick={() => onUebernehmen(i.slug)}
                >
                  {beschaeftigt === i.slug ? '…' : 'Übernehmen'}
                </button>
              )}
              {onSchalten && (
                <button
                  className="k-btn k-btn--geist"
                  disabled={beschaeftigt === i.slug}
                  onClick={() => {
                    const aktion = i.laeuft ? 'stoppen' : 'starten';
                    const frage = !i.laeuft
                      ? `Stack '${i.slug}' starten?`
                      : i.umgebung === 'PROD'
                      ? `ACHTUNG: '${i.slug}' ist als PRODUKTION markiert. Stoppen nimmt die live Instanz für alle Nutzer offline. Wirklich stoppen?`
                      : `Stack '${i.slug}' (Test) stoppen?`;
                    if (window.confirm(frage)) onSchalten(i.slug, aktion);
                  }}
                >
                  {i.laeuft ? 'Stoppen' : 'Starten'}
                </button>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default InstanzenPage;
